import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const toast = useRef(null);
    const [cartItems, setCartItems] = useState(() => {
        const saved = localStorage.getItem('ecommerce_cart');
        return saved ? JSON.parse(saved) : [];
    });
    const [cartVisible, setCartVisible] = useState(false);
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('ecommerce_favorites');
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        localStorage.setItem('ecommerce_cart', JSON.stringify(cartItems));
    }, [cartItems]);

    useEffect(() => {
        localStorage.setItem('ecommerce_favorites', JSON.stringify(favorites));
    }, [favorites]);

    const addToCart = useCallback((product, quantity = 1) => {
        setCartItems(prev => {
            // Normalizar producto
            const normalizedProduct = {
                ...product,
                id: product.id || product.idProducto,
                name: product.name || product.nombre || 'Producto sin nombre',
                price: product.price || product.precioVenta || 0,
                imagen: product.imagen || product.image,
                quantity
            };

            const existing = prev.find(item => item.id === normalizedProduct.id);
            if (existing) {
                return prev.map(item =>
                    item.id === normalizedProduct.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }
            return [...prev, normalizedProduct];
        });
        if (toast.current) {
            const productName = product.name || product.nombre || 'Producto';
            toast.current.show({
                severity: 'success',
                summary: '¡Producto agregado!',
                detail: `${productName} se agregó al carrito`,
                life: 2500
            });
        }
    }, []);

    const removeFromCart = useCallback((productId) => {
        setCartItems(prev => prev.filter(item => item.id !== productId));
    }, []);

    const updateQuantity = useCallback((productId, quantity) => {
        if (quantity <= 0) {
            removeFromCart(productId);
            return;
        }
        setCartItems(prev =>
            prev.map(item =>
                item.id === productId ? { ...item, quantity } : item
            )
        );
    }, [removeFromCart]);

    const clearCart = useCallback(() => {
        setCartItems([]);
    }, []);

    const getTotal = useCallback(() => {
        return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    }, [cartItems]);

    const getItemCount = useCallback(() => {
        return cartItems.reduce((count, item) => count + item.quantity, 0);
    }, [cartItems]);

    const toggleFavorite = useCallback((productId) => {
        setFavorites(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            }
            return [...prev, productId];
        });
    }, []);

    const isFavorite = useCallback((productId) => {
        return favorites.includes(productId);
    }, [favorites]);

    const value = {
        cartItems,
        cartVisible,
        setCartVisible,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotal,
        getItemCount,
        favorites,
        toggleFavorite,
        isFavorite
    };

    return (
        <CartContext.Provider value={value}>
            <Toast ref={toast} position="bottom-right" />
            {children}
        </CartContext.Provider>
    );
};
